'use client'

import {createContext, useContext, useState, useCallback} from 'react'
import debounce from 'lodash/debounce'
import setGameLikeStatusAction from '../set-like-dislike-action'
import triggerAnalytics from '@gamezop/analytics-sdk/client/trigger-analytics'

type LikeState = {
  likeDislikeStatus: boolean | null
  counts: {
    likeCount: number
    dislikeCount: number
  }
}

type GameLikeContextType = {
  likeState: LikeState
  handleLike: () => void
  handleDislike: () => void
}

const GameLikeContext = createContext<GameLikeContextType | undefined>(undefined)

type Props = {
  children: React.ReactNode
  isLikedInitially: boolean | null
  gameCode: string
  gameNameEn: string
  stats: {
    likeCount: number
    dislikeCount: number
  }
}

export const GameLikeProviderClient = ({children, isLikedInitially, gameCode, gameNameEn, stats}: Props) => {
  const [likeState, setLikeState] = useState<LikeState>({
    likeDislikeStatus: isLikedInitially,
    counts: {
      likeCount: stats.likeCount,
      dislikeCount: stats.dislikeCount,
    },
  })

  const debouncedUpdateLikeStatus = useCallback(
    debounce(async (newStatus: boolean | null) => {
      try {
        const result = await setGameLikeStatusAction({
          gameCode,
          isLiked: newStatus,
        })

        if (result.error) {
          console.error('Failed to update like status:', result.message)
          setLikeState({
            likeDislikeStatus: isLikedInitially,
            counts: {
              likeCount: stats.likeCount,
              dislikeCount: stats.dislikeCount,
            },
          })
        }
      } catch (error) {
        console.error('Unexpected error:', error)
        setLikeState({
          likeDislikeStatus: isLikedInitially,
          counts: {
            likeCount: stats.likeCount,
            dislikeCount: stats.dislikeCount,
          },
        })
      }
      triggerAnalytics('game-liked-clicked', {
        'liked-status': newStatus,
        'game-name': gameNameEn,
        'game-code': gameCode,
      }).sendAll()
    }, 500),
    [gameCode, isLikedInitially, stats],
  )

  const handleLike = () => {
    setLikeState((prevState) => {
      const prevLikeDislikeStatus = prevState.likeDislikeStatus
      const newStatus = prevLikeDislikeStatus === true ? null : true
      const newCounts = {...prevState.counts}

      if (prevLikeDislikeStatus) {
        newCounts.likeCount = Math.max(0, newCounts.likeCount - 1)
      } else {
        newCounts.likeCount += 1
        if (prevLikeDislikeStatus === false) {
          newCounts.dislikeCount = Math.max(0, newCounts.dislikeCount - 1)
        }
      }

      debouncedUpdateLikeStatus(newStatus)
      return {
        likeDislikeStatus: newStatus,
        counts: newCounts,
      }
    })
  }

  const handleDislike = () => {
    setLikeState((prevState) => {
      const prevLikeDislikeStatus = prevState.likeDislikeStatus
      const newStatus = prevLikeDislikeStatus === false ? null : false
      const newCounts = {...prevState.counts}

      if (prevLikeDislikeStatus === false) {
        newCounts.dislikeCount = Math.max(0, newCounts.dislikeCount - 1)
      } else {
        newCounts.dislikeCount += 1
        if (prevLikeDislikeStatus) {
          newCounts.likeCount = Math.max(0, newCounts.likeCount - 1)
        }
      }

      debouncedUpdateLikeStatus(newStatus)
      return {
        likeDislikeStatus: newStatus,
        counts: newCounts,
      }
    })
  }

  return <GameLikeContext.Provider value={{likeState, handleLike, handleDislike}}>{children}</GameLikeContext.Provider>
}

export const useGameLiked = () => {
  const context = useContext(GameLikeContext)
  if (!context) {
    throw new Error('useGameLiked must be used within a GameLikeProvider')
  }
  return context
}
